<script setup lang="ts">
    import { computed } from 'vue'
    import { useImageFallback } from 'composables'

    const props = withDefaults(
        defineProps<{
            name?: string
            imgSrc?: string | null
            modifiers?: string
            /** Shown when there is no image (or it is broken) and no name. */
            fallbackIcon?: string
        }>(),
        {
            name: undefined,
            imgSrc: undefined,
            modifiers: undefined,
            fallbackIcon: 'ri:user-line',
        },
    )

    // OAuth avatar urls expire and uploaded logos get deleted: without this the
    // browser paints its broken-image glyph inside the circle. Keying by url
    // also means a new/refreshed src is retried instead of staying broken.
    const { canShowImage, onImageError } = useImageFallback()
    const hasImage = computed(() => canShowImage(props.imgSrc))

    const avatarModifiers = computed(() => {
        const extra = hasImage.value ? 'transparent' : props.modifiers
        return extra ? `rounded ${extra}` : 'rounded'
    })

    const initials = computed(() => {
        if (!props.name) {
            return ''
        }
        return props.name
            .split(' ')
            .slice(0, 2)
            .map((n) => n.charAt(0).toUpperCase())
            .join('')
    })
</script>

<template>
    <VvAvatar
        class="pk-avatar"
        :img-src="hasImage ? (imgSrc as string) : undefined"
        :modifiers="avatarModifiers">
        <template #default>
            <img
                v-if="hasImage"
                :src="imgSrc as string"
                alt="avatar"
                @error="onImageError(imgSrc)" />
            <template v-else-if="initials">{{ initials }}</template>
            <VvIcon v-else-if="fallbackIcon" :name="fallbackIcon" />
        </template>
    </VvAvatar>
</template>

<style lang="scss">
    .pk-avatar {
        // Users can upload any image shape: always crop it to the circle
        // (the inner img inherits object-fit from the avatar root)
        object-fit: cover;
        overflow: hidden;

        // The placeholder icon should fill the circle like the initials it
        // replaces, not sit in it as a tiny glyph
        > .vv-icon {
            font-size: 1.4em;
        }
    }
</style>
